// ========== UI 工具函数 ==========
import { S } from './state.js';
import { CLASSES } from './data/classes.js';
import { getEquipById, TIERS, EQUIPMENT_DB } from './data/equipment.js';
import { EQUIP_SVG } from './equipment_art.js';

// toast 通知
export function toast(msg, dur) {
  const t = document.getElementById('toast');
  t.textContent = msg;
  t.classList.add('show');
  setTimeout(()=>t.classList.remove('show'), dur||1800);
}

// 掉落通知
export function showDropNotify(item) {
  const el = document.getElementById('dropNotify');
  const tierInfo = TIERS[item.tier];
  let html = `🎁 掉落了 <span class="drop-item-name" style="color:${tierInfo.color}">[${tierInfo.name}] ${item.name}</span>`;
  if(item.quote) {
    html += `<div style="font-size:10px;color:var(--dim);margin-top:4px;font-style:italic;">「${item.quote}」</div>`;
  }
  el.innerHTML = html;
  el.classList.add('show');
  setTimeout(()=>el.classList.remove('show'), 4500);
}

// 物品图标
export const ITEM_ICONS = {
  weapon: '⚔️', helm: '🪖', amulet: '📿', bracelet: '⭕',
  armor: '🛡️', ring: '💍', belt: '🔗', pants: '👖', boots: '👢',
  hp_potion_s: '🧪', hp_potion_m: '🧪', hp_potion_l: '🧪',
  mp_potion_s: '💠', antidote: '🌿',
  item: '📦',
};

export function getItemIcon(item) {
  if(!item) return '<span class="eq-icon-wrap"><span class="eq-icon-inner">📦</span></span>';
  const tier = item.tier || 'common';
  
  // 史诗装备：PNG优先，SVG为fallback
  if(tier === 'epic' && EQUIP_SVG[item.id]) {
    return `<span class="eq-icon-wrap eq-tier-epic eq-icon-svg">
      <img src="assets/equip_${item.id}.png" class="eq-epic-png" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" alt="${item.name}">
      <span class="eq-svg-art" style="width:44px;height:44px;display:none;align-items:center;justify-content:center;">${EQUIP_SVG[item.id]}</span>
    </span>`;
  }
  
  const raw = ITEM_ICONS[item.slot] || ITEM_ICONS[item.id] || '📦';
  const tierGem = { rare:'💠', unique:'💎', epic:'👑' };
  const gem = tierGem[tier] || '';
  return `<span class="eq-icon-wrap eq-tier-${tier}">
    <span class="eq-icon-inner">${raw}</span>
    ${gem ? `<span class="eq-tier-gem">${gem}</span>` : ''}
  </span>`;
}

export function getSlotIcon(slot) { return ITEM_ICONS[slot] || '📦'; }

// 怪物SVG fallback
export function getMonsterFallbackSVG(id) {
  const color = '#aaa'; // simplified - would normally call getMonsterData
  const isBoss = false;
  return `<svg viewBox="0 0 100 120" style="width:110px;height:130px;">
<ellipse cx="50" cy="110" rx="${isBoss?32:24}" ry="5" fill="#050505" opacity="0.4"/>
<ellipse cx="50" cy="70" rx="${isBoss?22:16}" ry="${isBoss?28:22}" fill="${color}" opacity="0.7"/>
<ellipse cx="50" cy="48" rx="${isBoss?18:14}" ry="${isBoss?16:12}" fill="${color}" opacity="0.85"/>
<ellipse cx="44" cy="46" rx="3" ry="4" fill="#fff" opacity="0.8"/>
<ellipse cx="56" cy="46" rx="3" ry="4" fill="#fff" opacity="0.8"/>
<circle cx="44" cy="45" r="1.5" fill="#111"/>
<circle cx="56" cy="45" r="1.5" fill="#111"/>
${isBoss?`<circle cx="50" cy="30" r="10" fill="none" stroke="${color}" stroke-width="2" opacity="0.5"/><circle cx="50" cy="30" r="5" fill="${color}" opacity="0.2"/>`:''}
</svg>`;
}

// 胜利画面
export function getVictoryImage(monsterId) {
  const map = {
    goblin_chief:'assets/monster_goblin_chief.png',
    thunder_boss:'assets/monster_thunder_boss.png',
    poison_slime:'assets/monster_poison.png',
    tau_chief:'assets/monster_tau_chief.png',
    flame_boss:'assets/monster_flame_boss.png',
    bone_dragon:'assets/monster_bone_dragon.png',
    golem_guard:'assets/monster_golem.png',
    ice_witch:'assets/monster_ice_witch.png',
    angel_fallen:'assets/monster_fallen_angel.png',
    sky_dragon:'assets/monster_sky_dragon.png',
    arad_demon:'assets/monster_arad_demon.png',
    forest_guardian:'assets/monster_forest_guardian.png',
    mad_alchemist:'assets/monster_alchemist.png',
    wind_elemental:'assets/monster_wind_elemental.png',
  };
  return map[monsterId] || '';
}

// DOM 动画
export function shakeElement(el, intensity) {
  if(!el) return;
  el.style.transition = 'none';
  el.style.transform = `translate(${(Math.random()-0.5)*intensity}px,${(Math.random()-0.5)*intensity}px)`;
  setTimeout(() => { el.style.transition = 'transform 0.15s ease'; el.style.transform = ''; }, 50);
}

export function showDmgNumber(el, dmg, color) {
  const span = document.createElement('span');
  span.textContent = dmg;
  span.style.cssText = `position:absolute;color:${color||'#c0392b'};font-weight:bold;font-size:18px;pointer-events:none;z-index:30;animation:dmgFloat 0.8s ease-out forwards;`;
  span.style.left = '50%'; span.style.top = '40%';
  el.appendChild(span);
  setTimeout(()=>span.remove(), 800);
}

export function hitStop(dur) {
  document.body.style.animation = `hitStop ${dur}ms ease`;
  setTimeout(() => document.body.style.animation = '', dur);
}

// 装备属性计算
export function getEquipStats() {
  let bonusAtk=0, bonusDef=0, bonusStr=0, bonusInt=0, bonusSpd=0;
  if(!S.equipment) S.equipment = {};
  if(!S.weaponPenalties) S.weaponPenalties = {};
  S.weaponPenalties = {};
  const clsWeaponType = S.cls ? (CLASSES[S.cls]?.weaponType || null) : null;
  for(const slot in S.equipment) {
    const eqId = S.equipment[slot];
    const item = getEquipById(eqId);
    if(!item) continue;
    let atkMul = 1, defMul = 1, spdMul = 1;
    if(item.weaponType && clsWeaponType && item.weaponType !== clsWeaponType) {
      atkMul = 0.7; defMul = 0.7; spdMul = 0.7;
      S.weaponPenalties[slot] = true;
    }
    if(item.atk) bonusAtk += Math.floor(item.atk * atkMul);
    if(item.def) bonusDef += Math.floor(item.def * defMul);
    if(item.spd) bonusSpd += Math.floor(item.spd * spdMul);
    if(item.str) bonusStr += item.str;
    if(item.int) bonusInt += item.int;
  }
  if(S.enhanceLevels) {
    for(const slot in S.enhanceLevels) {
      const lv = S.enhanceLevels[slot];
      if(lv > 0) { bonusAtk += Math.floor((lv+1)*1.5); bonusDef += Math.floor(lv*0.8); }
    }
  }
  if(S.altarBuff) { bonusAtk += 5; bonusDef += 3; }
  if(S.npcBoosts) { bonusAtk += (S.npcBoosts.atk||0); bonusDef += (S.npcBoosts.def||0); bonusSpd += (S.npcBoosts.spd||0); }
  return { bonusAtk, bonusDef, bonusStr, bonusInt, bonusSpd };
}

export function isWeaponMismatch(item) {
  if(!item || !item.weaponType) return false;
  const clsWeaponType = S.cls ? (CLASSES[S.cls]?.weaponType || null) : null;
  return clsWeaponType && item.weaponType !== clsWeaponType;
}

export function rollEquipmentDrop(monsterLv) {
  const adminCfg = window.__adminCfg || {};
  const tierRoll = Math.random();
  let tier;
  
  if(adminCfg.dropRates) {
    // 管理后台自定义精确概率
    const r = adminCfg.dropRates;
    const epicP = (r.epic || 2) / 100;
    const uniqueP = (r.unique || 10) / 100;
    const rareP = (r.rare || 23) / 100;
    const fineP = (r.fine || 35) / 100;
    // common 吸收剩余确保总和=1
    if(tierRoll < epicP) tier = 'epic';
    else if(tierRoll < epicP + uniqueP) tier = 'unique';
    else if(tierRoll < epicP + uniqueP + rareP) tier = 'rare';
    else if(tierRoll < epicP + uniqueP + rareP + fineP) tier = 'fine';
    else tier = 'common';
  } else {
    // 旧版倍率模式（兼容）
    const boost = adminCfg.dropBoost || 1;
    let epicP = 0.02 * boost;
    let uniqueP = 0.10 * boost;
    let rareP = 0.23 * Math.sqrt(boost);
    const sumHigh = epicP + uniqueP + rareP;
    const remaining = Math.max(0, 1 - sumHigh);
    const fineP = remaining * 0.55;
    
    if(tierRoll < epicP) tier = 'epic';
    else if(tierRoll < epicP + uniqueP) tier = 'unique';
    else if(tierRoll < epicP + uniqueP + rareP) tier = 'rare';
    else if(tierRoll < epicP + uniqueP + rareP + fineP) tier = 'fine';
    else tier = 'common';
  }
  const lvRange = Math.max(1, monsterLv - 3) + Math.floor(Math.random() * 7);
  const candidates = EQUIPMENT_DB.filter(e => e.tier === tier && e.lv >= lvRange - 3 && e.lv <= lvRange + 3);
  if(candidates.length === 0) return null;
  const item = candidates[Math.floor(Math.random() * candidates.length)];
  if(!S.inventory) S.inventory = [];
  const invEntry = { id: item.id, type: 'equip', tier: item.tier };
  // 稀有+装备随机获得词条
  if(['rare','unique','epic'].includes(item.tier) && window.rollAffixesForItem) {
    const affixes = window.rollAffixesForItem(item.tier);
    if(affixes.length > 0) invEntry.affixes = affixes;
  }
  S.inventory.push(invEntry);
  showDropNotify(item);
  return item;
}

export function getAtkEffectDesc(effect) {
  const map = {
    holyLight: '对 BOSS 伤害 +20%',
    chaosBlade: '5% 概率造成双倍伤害',
    darkGrail: '12% 概率额外 +15 暗属性伤害',
    truthLight: '对 BOSS 伤害 +25%'
  };
  return map[effect] || '未知效果';
}

export function getItemDescText(item) {
  if(!item) return '';
  let txt = item.desc || '';
  const tierInfo = TIERS[item.tier];
  if(tierInfo && tierInfo.name) txt += ` [${tierInfo.name}]`;
  if(item.lv) txt += ` Lv.${item.lv}`;
  return txt;
}

// ── 怪物图片映射 ──
export function getMonsterImageMap() {
  return {
    goblin:'monster_goblin', goblin2:'monster_goblin2', goblin_chief:'monster_goblin_chief',
    tau_beast:'monster_tau', tau_chief:'monster_tau_chief',
    zombie:'monster_zombie', zombie2:'monster_zombie2',
    thunder_goblin:'monster_thunder', thunder_boss:'monster_thunder_boss',
    poison_slime:'monster_poison', flame_goblin:'monster_flame', flame_boss:'monster_flame_boss',
    bone_dragon:'monster_bone_dragon', cursed_knight:'monster_cursed_knight',
    shadow_mage:'monster_shadow_mage', golem_guard:'monster_golem',
    ice_witch:'monster_ice_witch', cloud_gargoyle:'monster_gargoyle',
    storm_hawk:'monster_storm_hawk', angel_fallen:'monster_fallen_angel',
    sky_dragon:'monster_sky_dragon', arad_demon:'monster_arad_demon',
    cat_demon:'monster_cat_demon', goblin_scout:'monster_goblin_scout',
    wild_boar:'monster_wild_boar', swamp_zombie:'monster_swamp_zombie',
    dark_cat:'monster_dark_cat', goblin_shaman:'monster_goblin_shaman',
    forest_guardian:'monster_forest_guardian',
    puppet_master:'monster_puppet', dark_elf_archer:'monster_dark_elf',
    gbl_zealot:'monster_gbl', dark_sorcerer:'monster_sorcerer',
    stone_golem:'monster_stone_golem', mad_alchemist:'monster_alchemist',
    dragon_warrior:'monster_dragon_warrior', sky_patrol:'monster_sky_patrol',
    shadow_spirit:'monster_shadow_spirit', light_knight:'monster_light_knight',
    wind_elemental:'monster_wind_elemental',
    dark_elf_soldier:'monster_dark_elf_soldier', shadow_beast:'monster_shadow_beast',
    ghost_knight:'monster_ghost_knight', dark_priest:'monster_dark_priest',
    spider_queen:'monster_spider_queen', shadow_lord:'monster_shadow_lord',
    dark_knight_sky:'monster_dark_knight_sky', corridor_mage:'monster_corridor_mage',
    dragon_soldier:'monster_dragon_soldier', dragon_centurion:'monster_dragon_centurion',
  };
}

export function getBossDefeatedImage(monsterId) {
  const map = getMonsterImageMap();
  const file = map[monsterId];
  if(!file) return getMonsterFallbackSVG(monsterId);
  return `<img src="assets/monster_${monsterId}_defeated.png" 
    style="width:130px;height:150px;object-fit:contain;filter:brightness(0.6) saturate(0.4);transform:rotate(-8deg);opacity:0.85;"
    onerror="this.src='assets/${file}.png';this.style.filter='brightness(0.3) saturate(0.2) blur(1px)';this.style.transform='rotate(-15deg) scale(1.05)';this.style.opacity='0.7';"
    alt="${monsterId} defeated">`;
}
